import React, { useState, useEffect } from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Chip,
  Box,
  Typography,
  CircularProgress
} from '@mui/material';
import { paymentAPI } from '../../services/api';
import toast from 'react-hot-toast';
import moment from 'moment';

const PaymentList = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState({
    status: '',
    payment_method: ''
  });

  const fetchTransactions = async () => {
    setLoading(true);
    try {
      const response = await paymentAPI.getTransactions({
        page: page + 1,
        limit: rowsPerPage,
        ...filters
      });
      setTransactions(response.data.data);
      setTotal(response.data.pagination.total);
    } catch (error) {
      toast.error('Failed to fetch transactions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTransactions();
  }, [page, rowsPerPage, filters]);

  const getStatusChip = (status) => {
    const colors = {
      completed: 'success',
      pending: 'warning',
      failed: 'error',
      cancelled: 'default'
    };
    return <Chip label={status} color={colors[status] || 'default'} size="small" />;
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" fontWeight="bold">
          Payment Transactions
        </Typography>
        <Button variant="contained" onClick={fetchTransactions}>
          Refresh
        </Button>
      </Box>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Box display="flex" gap={2}>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={filters.status}
              label="Status"
              onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="completed">Completed</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="failed">Failed</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Payment Method</InputLabel>
            <Select
              value={filters.payment_method}
              label="Payment Method"
              onChange={(e) => setFilters(prev => ({ ...prev, payment_method: e.target.value }))}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="esewa">eSewa</MenuItem>
              <MenuItem value="khalti">Khalti</MenuItem>
              <MenuItem value="connectips">ConnectIPS</MenuItem>
              <MenuItem value="hbl">HBL</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Paper>

      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Transaction ID</TableCell>
                <TableCell>User</TableCell>
                <TableCell>Package</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Payment Method</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Date</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : transactions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    No transactions found
                  </TableCell>
                </TableRow>
              ) : (
                transactions.map((tx) => (
                  <TableRow key={tx.id}>
                    <TableCell>{tx.transaction_uuid?.substring(0, 20)}...</TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2">
                          {tx.first_name} {tx.last_name}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {tx.phone}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>{tx.package_name || 'N/A'}</TableCell>
                    <TableCell>₨ {tx.amount}</TableCell>
                    <TableCell>{tx.payment_method?.toUpperCase()}</TableCell>
                    <TableCell>{getStatusChip(tx.status)}</TableCell>
                    <TableCell>{moment(tx.created_at).format('YYYY-MM-DD HH:mm')}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 20, 50, 100]}
          component="div"
          count={total}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </Paper>
    </Box>
  );
};

export default PaymentList;